import NoImpactBanner from "@/components/NoImpactBanner/NoImpactBanner";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import { useCompanyAccess } from "@/hooks/useCompanySettings";
import styles from "@/pages/dashboard/Dashboard.module.scss";
import { Button } from "antd";
import { Link, useNavigate } from "react-router-dom";
import MetricSelector from "../components/MetricSelector/MetricSelector";
import YearPicker from "../components/YearPicker/YearPicker";
import { useCustomerImpactTotals } from "../hooks/useCustomerImpact";
import { useDashboardYear } from "../hooks/useDateRange";
import useImpactMetric from "../hooks/useImpactMetric";
import { useDashboardOrderCounts } from "../hooks/usePoSo";
import CustomerImpactPerScope from "./widgets/CustomerImpactPerScope";
import CustomerImpactPerType from "./widgets/CustomerImpactPerType";
import CustomerImpactTopContributors from "./widgets/CustomerImpactTopContributors";
import CustomerImpactTotal from "./widgets/CustomerImpactTotal";
import PremiumExplore from "@/components/PremiumFeatureComponents/PremiumExplore";

export default function CustomerDashboard() {
  const metric = useImpactMetric();
  const navigate = useNavigate();
  const access = useCompanyAccess();

  const { data: orderCounts } = useDashboardOrderCounts({});
  const { data: totalImpact } = useCustomerImpactTotals({ metrics: [metric.key] });
  const { year } = useDashboardYear();
  const pageTitle = `Customer impact`;
  const pageDescription =
    "Insight into the part of your total company impact attributed to customers.";
  if (access && !access.includes("lca"))
    return (
      <PremiumExplore
        title={pageTitle}
        description={pageDescription}
      />
    );

  return (
    <div>
      <PageHeader
        title={pageTitle}
        description={pageDescription}
        right={
          <>
            <YearPicker />
          </>
        }
      />
      <MetricSelector
        value={metric.key}
        onChange={(key) => navigate("/dashboard/customer/" + key)}
      />
      {orderCounts?.po === 0 ? (
        <NoImpactBanner title={`No customer impact for ${year}`}>
          <span>
            Start with uploading purchase orders for {year}, so we can calculate your customer
            impact.
          </span>
          <Link to="/orders/purchase_orders">
            <Button type="primary">Upload orders</Button>
          </Link>
        </NoImpactBanner>
      ) : totalImpact?.customer_impact[metric.key] === 0 ? (
        <NoImpactBanner title={`No customer impact for ${year}`}>
          <span>What to do?</span>
          <span>For the related products:</span>
          <ul>
            <li>
              Determine <Link to="/usage">usage</Link>
            </li>
            <li>
              Define <Link to="/eol">end of life</Link>
            </li>
          </ul>
        </NoImpactBanner>
      ) : (
        <div className={styles.grid}>
          <div className={styles["two-columns"]}>
            <div>
              <CustomerImpactTotal metric={metric} />
              {metric.key === "carbon_footprint" && <CustomerImpactPerScope />}
            </div>
            <div>
              <CustomerImpactTopContributors metric={metric} />
            </div>
          </div>
          <div className={styles["one-column"]}>
            <CustomerImpactPerType metric={metric} />
          </div>
        </div>
      )}
    </div>
  );
}
